// React and core libraries
import React, { useEffect, useState, lazy, Suspense, useCallback } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import * as Sentry from "@sentry/react";

// UI Components
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Logo } from "@/components/ui/logo";
import { OfflineIndicator } from "@/components/ui/offline-indicator";
import { NetworkStatusBanner } from "@/components/ui/network-status-banner";
import { ErrorFallback } from "@/components/ErrorFallback";
import { ScrollToTop } from "@/components/ScrollToTop";
import { LoadingState } from "@/components/ui/LoadingState";
import MobileDesktopNotice from "@/components/ui/MobileDesktopNotice";
import { lazyWithRetry, LoadingState as LazyLoadingState } from "@/components/ui/lazy-loader";
import PageTransitionLoader from "@/components/ui/PageTransitionLoader";

// Layout Components
import { PageShell, type PageShellOptions } from "@/components/layout/PageShell";
import { ConditionalLayout } from "@/components/layout/ConditionalLayout";
import { BuyerLayout } from "./layouts/BuyerLayout";
import { SupplierLayout } from "./layouts/SupplierLayout";
import { AdminLayout } from "./layouts/AdminLayout";

// Contexts
import { AuthProvider } from "@/contexts/AuthContext";
import { ResponsiveProvider } from "@/contexts/ResponsiveContext";
import { ImpersonationProvider } from "@/contexts/ImpersonationContext";
import { PortalProvider } from "@/contexts/PortalContext";

// Hooks
import { useAuth } from "@/hooks/useAuth";
import { usePageTracking } from "@/hooks/usePageTracking";
import { useSessionTracking } from "@/hooks/useSessionTracking";

// Utilities and Libraries
import { initGA4, initWebVitals } from "@/lib/analytics";
import { logger } from "@/lib/logger";
import { captureError } from "@/lib/error-tracking";
import { supabase } from "@/integrations/supabase/client";
import { MARKETING_URL } from "@/lib/urls";

// Config and Types
import { USER_ROLES } from "@/config/roles";
import { FEATURE_FLAGS } from "@/config/featureFlags";
import { isSupabaseConfigured, missingEnvVars } from "@/integrations/supabase/client";

// Components
import { FirstTimeWizard } from "@/components/onboarding/FirstTimeWizard";
import { KeyboardShortcutsProvider } from "@/components/KeyboardShortcutsProvider";
import { RoutePrefetcher } from "@/components/performance/RoutePrefetcher";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { ConfigurationError } from "@/components/ConfigurationError";
import { ProductionDebugger } from "@/debugger-system";

// Eager load critical routes
import Auth from "./pages/Auth";
import PendingReview from "./pages/PendingReview";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import CanvaCallback from "./components/canva/connect/CanvaCallback";
import CanvaReturn from "./pages/buyer/CanvaReturn";

// VideoProvider wrapper - loads Stream SDK only when user enters Messages (saves ~640KB on most page loads)
import { VideoContextShell } from "@/contexts/VideoContextShell";

interface VideoProviderState {
  VideoProvider: React.ComponentType<{ children: React.ReactNode; apiKey?: string }> | null;
  ready: boolean;
}

const VideoProviderWrapper = ({ children }: { children: React.ReactNode }) => {
  const [state, setState] = useState<VideoProviderState>({
    VideoProvider: null,
    ready: false,
  });
  const streamApiKey = import.meta.env.VITE_STREAM_API_KEY;
  const loadingRef = React.useRef(false);

  // Load Stream SDK only when Messages page requests it (saves ~640KB for users who never use video)
  React.useEffect(() => {
    const loadVideo = () => {
      if (loadingRef.current) return;
      loadingRef.current = true;
      Promise.all([
        import("@/contexts/VideoContext"),
        import("@stream-io/video-react-sdk/dist/css/styles.css"),
      ])
        .then(([m]) => {
          setState({ VideoProvider: m.VideoProvider, ready: true });
        })
        .catch((err) => {
          if (import.meta.env.DEV) {
            logger.warn('[App] Stream Video SDK failed to load', { error: err });
          }
          loadingRef.current = false;
          setState({ VideoProvider: null, ready: true });
        });
    };

    const fn = () => loadVideo();
    window.addEventListener('hs-load-video-context', fn);
    return () => window.removeEventListener('hs-load-video-context', fn);
  }, []);

  // Always render immediately - use shell (no Stream) until Messages loads full SDK
  if (state.VideoProvider) {
    return <state.VideoProvider apiKey={streamApiKey}>{children}</state.VideoProvider>;
  }
  return <VideoContextShell>{children}</VideoContextShell>;
};

// Redirect to marketing site for marketing routes
const MarketingRedirect = ({ path }: { path: string }) => {
  React.useEffect(() => {
    window.location.href = `${MARKETING_URL}${path}`;
  }, [path]);
  return (
    <div className="flex min-h-screen items-center justify-center">
      <p className="text-muted-foreground">Redirecting to holescale.com...</p>
    </div>
  );
};

// Lazy load buyer routes for better code splitting
const EnhancedBuyerDashboard = lazyWithRetry(() => import("@/pages/buyer/Dashboard").then(m => ({ default: m.Dashboard })));
const CreateRFQ = lazyWithRetry(() => import("@/pages/buyer/CreateRFQ").then(m => ({ default: m.default })));
const BuyerRFQDetail = lazyWithRetry(() => import("./pages/buyer/RFQDetail"));
const BulkRFQ = lazyWithRetry(() => import("./pages/buyer/BulkRFQ"));
const Suppliers = lazyWithRetry(() => import("./pages/buyer/Suppliers"));

// Lazy load routes with retry logic
const Favorites = lazyWithRetry(() => import("./pages/Favorites"));
const BuyerProfilePage = lazyWithRetry(() => import("./pages/buyer/BuyerProfilePage"));
const SupplierProfile = lazyWithRetry(() => import("./pages/buyer/SupplierProfile"));
const SupplierScorecard = lazyWithRetry(() => import("./pages/buyer/SupplierScorecard"));
const SupplierProfilePage = lazyWithRetry(() => import("./pages/supplier/SupplierProfilePage"));
const AdminProfilePage = lazyWithRetry(() => import("./pages/admin/AdminProfilePage"));
const MockupEditor = lazyWithRetry(() => import("./pages/buyer/MockupEditor"));
const RFQManagement = lazyWithRetry(() => import("./pages/RFQManagement"));
const RFQDetail = lazyWithRetry(() => import("./pages/RFQDetail"));
const SearchSuppliers = lazyWithRetry(() => import("./pages/SearchSuppliers"));
const Orders = lazyWithRetry(() => import("./pages/Orders"));
const OrderDetail = lazyWithRetry(() => import("./pages/OrderDetail"));
const AdminOrders = lazyWithRetry(() => import("./pages/admin/AdminOrders"));
const AdminRFQs = lazyWithRetry(() => import("./pages/admin/AdminRFQs"));
const TerritoryManagement = lazyWithRetry(() => import("./pages/admin/TerritoryManagement"));
const Settings = lazyWithRetry(() => import("./pages/Settings"));
const PostRFQ = lazyWithRetry(() => import("./pages/PostRFQ"));
const Messages = lazyWithRetry(() => import("./pages/Messages"));
const ProductsBrowse = lazyWithRetry(() => import("./pages/ProductsBrowse"));
const ProductDetail = lazyWithRetry(() => import("./pages/ProductDetail"));
const SupplierDashboard = lazyWithRetry(() => import("./pages/supplier/SupplierDashboard"));
const SupplierProducts = lazyWithRetry(() => import("./pages/supplier/SupplierProducts"));
const RFQMarketplace = lazyWithRetry(() => import("./pages/supplier/RFQMarketplace"));
const SupplierQuotes = lazyWithRetry(() => import("./pages/supplier/SupplierQuotes"));
const SupplierSettings = lazyWithRetry(() => import("./pages/supplier/SupplierSettings"));
const SupplierEarnings = lazyWithRetry(() => import("./pages/supplier/SupplierEarnings"));
const SupplierTerritory = lazyWithRetry(() => import("./pages/supplier/SupplierTerritory"));
const SupplierPricingTools = lazyWithRetry(() => import("./pages/supplier/PricingTools"));
const AuditLog = lazyWithRetry(() => import("./pages/admin/AuditLog"));
const SystemLogs = lazyWithRetry(() => import("./pages/admin/SystemLogs"));
const AdminWinLossAnalytics = lazyWithRetry(() => import("./pages/admin/WinLossAnalytics"));
const EmailManagement = lazyWithRetry(() => import("./pages/admin/EmailManagement"));
const EmailTemplateEditor = lazyWithRetry(() => import("./pages/admin/EmailTemplateEditor"));
const EmailTemplatePreview = lazyWithRetry(() => import("./pages/admin/EmailTemplatePreview"));
const Inventory = lazyWithRetry(() => import("./pages/supplier/Inventory"));
const SeedData = lazyWithRetry(() => import("./pages/admin/SeedData"));
const SeedComprehensiveData = lazyWithRetry(() => import("./pages/admin/SeedComprehensiveData"));
const SeedDataVerification = lazyWithRetry(() => import("./pages/admin/SeedDataVerification"));
const CleanupSeedData = lazyWithRetry(() => import("./pages/admin/CleanupSeedData"));
const SeedProductImages = lazyWithRetry(() => import("./pages/admin/SeedProductImages"));
const DataAudit = lazyWithRetry(() => import("./pages/admin/DataAudit"));
const TrendingManagement = lazyWithRetry(() => import("./pages/admin/TrendingManagement"));
const AdminDashboard = lazyWithRetry(() => import("./pages/admin/AdminDashboard"));
const AdminUsers = lazyWithRetry(() => import("./pages/admin/AdminUsers"));
const SupportTickets = lazyWithRetry(() => import("./pages/admin/SupportTickets"));
const SupportTicketDetail = lazyWithRetry(() => import("./pages/admin/SupportTicketDetail"));
const TrackingLookup = lazyWithRetry(() => import("./pages/admin/TrackingLookup"));
const VerificationQueue = lazyWithRetry(() => import("./pages/admin/VerificationQueue"));
const ReviewQueue = lazyWithRetry(() => import("./pages/admin/ReviewQueue"));
const ComplianceManagement = lazyWithRetry(() => import("./pages/admin/ComplianceManagement"));
const FeatureRequests = lazyWithRetry(() => import("./pages/FeatureRequests"));
const Status = lazyWithRetry(() => import("./pages/Status"));
const DataManagement = lazyWithRetry(() => import("./pages/admin/DataManagement"));
const BuyerDashboard = lazyWithRetry(() => import("./pages/buyer/BuyerDashboard"));
const BuyerOrders = lazyWithRetry(() => import("./pages/buyer/BuyerOrders"));
const Reorder = lazyWithRetry(() => import("./pages/buyer/Reorder"));
const TrackOrders = lazyWithRetry(() => import("./pages/buyer/TrackOrders"));
const Quotes = lazyWithRetry(() => import("./pages/buyer/Quotes"));
const CompareQuotes = lazyWithRetry(() => import("./pages/buyer/CompareQuotes"));
const BuyerSupport = lazyWithRetry(() => import("./pages/buyer/BuyerSupport"));
const BuyerSupportTicketDetail = lazyWithRetry(() => import("./pages/buyer/BuyerSupportTicketDetail"));
const BuyerAnalytics = lazyWithRetry(() => import("./pages/buyer/Analytics"));
const SpecWizard = lazyWithRetry(() => import("./pages/buyer/SpecWizard"));
const SavedSpecs = lazyWithRetry(() => import("./pages/buyer/SavedSpecs"));
const Clearance = lazyWithRetry(() => import("./pages/buyer/Clearance"));
// Glossary now redirects to marketing site
const VendorManagement = lazyWithRetry(() => import("./pages/buyer/VendorManagement"));
const BuyerContacts = lazyWithRetry(() => import("./pages/buyer/Contacts"));
const BuyerContactDetail = lazyWithRetry(() => import("./pages/buyer/Contacts/[id]"));
const SupplierCustomers = lazyWithRetry(() => import("./pages/supplier/Customers"));
const SupplierCustomerDetail = lazyWithRetry(() => import("./pages/supplier/Customers/[id]"));
const SupplierSupport = lazyWithRetry(() => import("./pages/supplier/SupplierSupport"));
const ExploreProducts = lazyWithRetry(() => import("./pages/buyer/ExploreProducts"));
const BulkDiscountCalculator = lazyWithRetry(() => import("./pages/buyer/BulkDiscountCalculator"));
const SpendForecasting = lazyWithRetry(() => import("./pages/buyer/SpendForecasting"));
const AutoQuoteRules = lazyWithRetry(() => import("./pages/supplier/AutoQuoteRules"));
const SupplierSupportTicketDetail = lazyWithRetry(() => import("./pages/supplier/SupplierSupportTicketDetail"));
const SupplierAnalytics = lazyWithRetry(() => import("./pages/supplier/SupplierAnalytics"));
const SupplierWinLossAnalytics = lazyWithRetry(() => import("./pages/supplier/WinLossAnalytics"));
const TeamAnalytics = lazyWithRetry(() => import("./pages/supplier/TeamAnalytics"));
const ProductionScheduling = lazyWithRetry(() => import("./pages/supplier/ProductionScheduling"));
const PaymentFailureAnalytics = lazyWithRetry(() => import("./pages/admin/PaymentFailureAnalytics"));
const SupplierVerification = lazyWithRetry(() => import("./pages/supplier/SupplierVerification"));
const SupplierOperationsHub = lazyWithRetry(() => import("./pages/supplier/OperationsHub"));
const SupplierOrders = lazyWithRetry(() => import("./pages/supplier/SupplierOrders"));
const PaymentMethods = lazyWithRetry(() => import("./pages/buyer/PaymentMethods"));
const Budgets = lazyWithRetry(() => import("./pages/buyer/Budgets"));
const Samples = lazyWithRetry(() => import("./pages/buyer/Samples"));
// Design mockups feature deprecated - routes removed
const BuyerProfile = lazyWithRetry(() => import("./pages/BuyerProfile"));
const VendorHistory = lazyWithRetry(() => import("./pages/buyer/VendorHistory"));
const SetupAdmin = lazyWithRetry(() => import("./pages/SetupAdmin"));
// Marketing pages now redirect to marketing site (holescale.com)
// Privacy, TermsOfService, PrivacyPolicy, Resources, Contact, Waitlist,
// About, Blog, FAQ, Investors, Pricing - all handled by MarketingRedirect
const RFQRouter = lazyWithRetry(() => import("./pages/RFQRouter"));
const ExecutiveDashboard = lazyWithRetry(() => import("./pages/admin/ExecutiveDashboard"));
const FinancialDashboard = lazyWithRetry(() => import("./pages/admin/FinancialDashboard"));
const PipelineDashboard = lazyWithRetry(() => import("./pages/admin/PipelineDashboard"));
const CustomerHealthDashboard = lazyWithRetry(() => import("./pages/admin/CustomerHealthDashboard"));
const ErrorLogs = lazyWithRetry(() => import("./pages/admin/ErrorLogs"));
const BuyerNotifications = lazyWithRetry(() => import("./pages/buyer/BuyerNotifications"));
const SupplierNotifications = lazyWithRetry(() => import("./pages/supplier/SupplierNotifications"));
const AdminNotifications = lazyWithRetry(() => import("./pages/admin/AdminNotifications"));
const NewBroadcast = lazyWithRetry(() => import("./pages/admin/NewBroadcast"));
const SecurityDashboard = lazyWithRetry(() => import("./pages/admin/SecurityDashboard"));
const FraudDetection = lazyWithRetry(() => import("./pages/admin/FraudDetection"));
const RecommendedSuppliers = lazyWithRetry(() => import("./pages/buyer/RecommendedSuppliers"));
const TestingDashboard = lazyWithRetry(() => import("./pages/admin/TestingDashboard"));
const VendorsPending = lazyWithRetry(() => import("./pages/admin/VendorsPending"));
const VendorApplicationsQueue = lazyWithRetry(() => import("./pages/admin/VendorApplicationsQueue"));
// Legacy vendor pages removed - use supplier portal instead
// VendorSignup → Use /auth with supplier role
// VendorDashboardPending → Use /supplier/dashboard
// SupplierProfileEdit → Use /supplier/profile
const AdminSettings = lazyWithRetry(() => import("./pages/admin/AdminSettings"));
const SystemStatus = lazyWithRetry(() => import("./pages/admin/SystemStatus"));
const BuyerResources = lazyWithRetry(() => import("./pages/buyer/BuyerResources"));
const SupplierResources = lazyWithRetry(() => import("./pages/supplier/SupplierResources"));
const BuyerHelp = lazyWithRetry(() => import("./pages/buyer/BuyerHelp"));
const SupplierHelp = lazyWithRetry(() => import("./pages/supplier/SupplierHelp"));
// Design mockups feature deprecated - routes removed
const SupplierSampleRequests = lazyWithRetry(() => import("./pages/supplier/SampleRequests"));
const SampleRequestDetail = lazyWithRetry(() => import("./pages/supplier/SampleRequestDetail"));
const SupplierSampleRequestsAnalyticsAndReports = lazyWithRetry(() => import("./pages/supplier/SampleRequestsAnalyticsAndReports"));
const BuyerSampleRequestsAnalytics = lazyWithRetry(() => import("./pages/buyer/SampleRequestsAnalytics"));
const EmailVerification = lazyWithRetry(() => import("./pages/EmailVerification"));
const TwoFactorSetup = lazyWithRetry(() => import("./pages/TwoFactorSetup"));
const UserProfile = lazyWithRetry(() => import("./pages/UserProfile"));
const AuthCallback = lazyWithRetry(() => import("./pages/AuthCallback"));
const ResetPasswordPage = lazyWithRetry(() => import("./pages/ResetPassword"));
const PopulateProductImages = lazyWithRetry(() => import("./pages/admin/PopulateProductImages"));
const VendorInventory = lazyWithRetry(() => import("./pages/supplier/VendorInventory"));
const ProductCatalog = lazyWithRetry(() => import("./pages/buyer/ProductCatalog"));
const SupplierComparison = lazyWithRetry(() => import("./pages/buyer/SupplierComparison"));
const BuyerProductDetail = lazyWithRetry(() => import("./pages/buyer/ProductDetail"));
const ProductOversight = lazyWithRetry(() => import("./pages/admin/ProductOversight"));
const BuyerInventory = lazyWithRetry(() => import("./pages/buyer/BuyerInventory"));
const MockupStudio = lazyWithRetry(() => import("./pages/buyer/MockupStudio"));
const LogoLibrary = lazyWithRetry(() => import("./pages/buyer/LogoLibrary"));
const DesignGallery = lazyWithRetry(() => import("./pages/buyer/DesignGallery"));
const ArtFileManager = lazyWithRetry(() => import("./pages/buyer/ArtFileManager"));
const CRMIntegration = lazyWithRetry(() => import("./pages/buyer/CRMIntegration"));
const DesignStudio = FEATURE_FLAGS.designStudioV1
  ? lazyWithRetry(() => import("./pages/buyer/DesignStudio"))
  : null;
const InventoryAnalytics = lazyWithRetry(() => import("./pages/buyer/InventoryAnalytics"));
const BuyerSettings = lazyWithRetry(() => import("./pages/buyer/BuyerSettings"));
const TeamManagement = lazyWithRetry(() => import("./pages/buyer/TeamManagement"));
const WarehouseDashboard = lazyWithRetry(() => import("./pages/buyer/WarehouseDashboard"));
const ProcurementOps = lazyWithRetry(() => import("./pages/buyer/ProcurementOps"));
const FavoriteProducts = lazyWithRetry(() => import("./pages/FavoriteProducts"));
const ModelLibrary = lazyWithRetry(() => import("./pages/admin/ModelLibrary"));
const FavoriteSuppliers = lazyWithRetry(() => import("./pages/buyer/FavoriteSuppliers"));
const ApprovedVendors = lazyWithRetry(() => import("./pages/buyer/ApprovedVendors"));
const CompliancePortal = lazyWithRetry(() => import("./pages/supplier/CompliancePortal"));
const CronMonitoring = lazyWithRetry(() => import("./pages/admin/CronMonitoring"));
const NavigationAudit = lazyWithRetry(() => import("./pages/admin/NavigationAudit"));
const AdminAnalytics = lazyWithRetry(() => import("./pages/admin/Analytics"));
const BuyerSearch = lazyWithRetry(() => import("./pages/buyer/Search"));
const SupplierSearch = lazyWithRetry(() => import("./pages/supplier/Search"));
const SupplierCRMExport = lazyWithRetry(() => import("./pages/supplier/CRMExport"));
const AdminSearch = lazyWithRetry(() => import("./pages/admin/Search"));
const BuyerCreditsDashboard = FEATURE_FLAGS.creditSystem
  ? lazyWithRetry(() => import("./pages/buyer/CreditsDashboard"))
  : null;
const SupplierCreditsDashboard = FEATURE_FLAGS.creditSystem
  ? lazyWithRetry(() => import("./pages/supplier/CreditsDashboard"))
  : null;

// Protected route wrapper with error boundary
const ProtectedRouteWrapper = ({ 
  children, 
  requiredRole 
}: { 
  children: React.ReactNode; 
  requiredRole: typeof USER_ROLES.BUYER | typeof USER_ROLES.SUPPLIER | typeof USER_ROLES.ADMIN 
}) => (
  <ErrorBoundary 
    FallbackComponent={ErrorFallback}
    onReset={() => window.location.reload()}
  >
    <Suspense fallback={<LazyLoadingState />}>
      <ProtectedRoute requiredRole={requiredRole}>
        {children}
      </ProtectedRoute>
    </Suspense>
  </ErrorBoundary>
);

// Public route wrapper with error boundary
const PublicRouteWrapper = ({
  children,
  pageShellOptions,
}: {
  children: React.ReactNode;
  pageShellOptions?: Partial<PageShellOptions>;
}) => (
  <ErrorBoundary
    FallbackComponent={ErrorFallback}
    onReset={() => window.location.reload()}
  >
    <Suspense fallback={<LazyLoadingState />}>
      <PageShell
        defaultOptions={{
          width: "wide",
          disableBottomPadding: true,
          className: "space-y-10",
          ...pageShellOptions,
        }}
      >
        {children}
      </PageShell>
    </Suspense>
  </ErrorBoundary>
);

// PERFORMANCE: Optimized QueryClient for fast initial load and smooth navigation
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 10, // 10 minutes - longer stale time reduces refetches
      gcTime: 1000 * 60 * 60, // 1 hour cache retention
      retry: (failureCount, error: unknown) => {
        // Don't retry on 404s or auth errors
        if (error && typeof error === 'object' && 'status' in error) {
          const statusError = error as { status?: number };
          if (statusError.status === 404 || statusError.status === 401) return false;
        }
        return failureCount < 1; // Only 1 retry for faster failure
      },
      retryDelay: 500, // Quick retry
      refetchOnWindowFocus: false, // Prevent jarring refetches
      refetchOnReconnect: true,
      // Enable structural sharing to prevent unnecessary re-renders
      structuralSharing: true,
      // PERFORMANCE: Use placeholder data during refetches
      placeholderData: (previousData: unknown) => previousData,
      // Always run queries regardless of React Query's online detection.
      // 'offlineFirst'/'online' can leave queries permanently "paused" when the
      // browser's online state is briefly/incorrectly reported as offline
      // (embedded/automation browsers, flaky networks), which silently rendered
      // dashboards as all-zeros. 'always' guarantees the fetch fires.
      networkMode: 'always',
    },
    mutations: {
      // PERFORMANCE: Quick retry for mutations
      retry: 1,
      retryDelay: 300,
      networkMode: 'always',
    },
  },
});

function AppContent() {
  const { loading, user } = useAuth();

  // Define routes that don't require auth loading (public routes)
  // These routes should render immediately without waiting for auth state
  const getIsPublicRoute = () => {
    if (typeof window === 'undefined') return false;
    const pathname = window.location.pathname;
    const publicRoutes = [
      '/',
      '/contact',
      '/waitlist',
      '/for-buyers',
      '/for-suppliers',
      '/how-it-works',
      '/features',
      '/resources',
      '/about',
      '/blog',
      '/faq',
      '/terms',
      '/privacy',
      '/cookies',
      '/pricing',
      '/investors',
      '/feature-requests',
      '/auth',
      '/products',
    ];
    
    // Check exact matches
    if (publicRoutes.includes(pathname)) return true;
    
    // Check route patterns for public routes
    if (pathname.startsWith('/auth/')) return true;
    if (pathname.startsWith('/product/')) return true; // Product pages are public
    if (pathname.startsWith('/rfq/') && !pathname.startsWith('/rfq/new')) return true;
    if (pathname.startsWith('/search/')) return true;
    if (pathname.startsWith('/glossary')) return true;
    // Public profile views (not dashboard routes)
    if (pathname.match(/^\/supplier\/[^/]+$/) && !pathname.startsWith('/supplier/dashboard')) return true;
    if (pathname.match(/^\/buyer\/[^/]+$/) && !pathname.startsWith('/buyer/dashboard')) return true;
    
    return false;
  };

  // Check if route is a protected route (requires auth)
  const getIsProtectedRoute = () => {
    if (typeof window === 'undefined') return false;
    const pathname = window.location.pathname;
    // Protected routes are under /buyer/, /supplier/, /admin/ (except public profile views)
    return (
      pathname.startsWith('/buyer/') ||
      pathname.startsWith('/supplier/') ||
      pathname.startsWith('/admin/')
    );
  };

  const isPublicRoute = getIsPublicRoute();
  const isProtectedRoute = getIsProtectedRoute();

  // Runtime diagnostic check for Sentry configuration
  useEffect(() => {
    // Sentry diagnostic checks removed - use proper logging instead
  }, []);

  // Test Sentry error - DISABLED (can be re-enabled for testing)
  // To re-enable: uncomment this code and set VITE_ENABLE_SENTRY=true in .env.local
  /*
  useEffect(() => {
    // Only trigger test error once on mount in development when Sentry is enabled
    if (import.meta.env.DEV && import.meta.env.VITE_ENABLE_SENTRY === 'true') {
      // Trigger after a short delay to ensure Sentry is initialized
      const timer = setTimeout(() => {
        // Send a test error to verify Sentry is working
        // This helps ensure error tracking is functional during development
        const testError = new Error('🧪 Test Sentry Error - This is intentional for testing error tracking');
        const eventId = Sentry.captureException(testError, {
          tags: {
            test: true,
            component: 'App',
            action: 'test_error_auto_trigger',
          },
          level: 'error',
        });
        
        // Also send a test message
        Sentry.captureMessage('🧪 Test message sent to Sentry', {
          level: 'info',
          tags: {
            test: true,
            component: 'App',
          },
        });
        
        // Flush to ensure it's sent immediately
        Sentry.flush(2000);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, []);
  */
  const [showWelcome, setShowWelcome] = useState(false);
  const [userRole, setUserRole] = useState<"buyer" | "supplier" | "admin" | null>(null);
  
  // Initialize analytics on mount
  useEffect(() => {
    // Use the new GA4 measurement ID for app.holescale.com
    const gaId = import.meta.env.VITE_GA4_MEASUREMENT_ID || 'G-ZZC0H6XJXS';
    // Note: gtag is already loaded in index.html, but initGA4 will handle setup if needed
    initGA4(gaId);
    // Initialize Core Web Vitals monitoring
    initWebVitals();
  }, []);

  const checkFirstTimeUser = useCallback(async () => {
    if (!user) return;

    try {
      // Use Promise.allSettled to handle partial failures gracefully
      const [profileResult, rolesResult] = await Promise.allSettled([
        supabase
          .from("profiles")
          .select("onboarding_completed")
          .eq("id", user.id)
          .maybeSingle(),
        supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", user.id)
          .maybeSingle()
      ]);

      // Extract results, handling rejections
      const profile = profileResult.status === 'fulfilled' ? profileResult.value.data : null;
      const roles = rolesResult.status === 'fulfilled' ? rolesResult.value.data : null;

      // Log any failures for debugging
      if (profileResult.status === 'rejected') {
        logger.warn("Failed to fetch profile for first-time check", { reason: profileResult.reason });
      }
      if (rolesResult.status === 'rejected') {
        logger.warn("Failed to fetch roles for first-time check", { reason: rolesResult.reason });
      }

      if (profile && !profile.onboarding_completed && roles) {
        setUserRole(roles.role as "buyer" | "supplier" | "admin");
        setShowWelcome(true);
      }
    } catch (error) {
      logger.error("Error checking first time user", {
        error,
        userId: user?.id,
        component: "App"
      });
    }
  }, [user]);

  useEffect(() => {
    checkFirstTimeUser();
  }, [checkFirstTimeUser]);

  const handleWelcomeComplete = async () => {
    if (user) {
      await supabase
        .from("profiles")
        .update({ onboarding_completed: true })
        .eq("id", user.id);
    }
    setShowWelcome(false);
  };

  const handleWelcomeSkip = async () => {
    await handleWelcomeComplete();
  };

  const RouterTracker = () => {
    usePageTracking(); // Track page views on route changes
    useSessionTracking();
    return null;
  };

  // OPTIMIZED: Only show loading skeleton for protected routes that require auth
  // Public routes (like /product/:id) should render immediately without waiting
  // This prevents unnecessary loading screens on public pages
  if (loading && isProtectedRoute && !isPublicRoute) {
    return (
      <div className="flex h-screen w-full bg-background duration-200 animate-in fade-in">
        {/* Sidebar skeleton */}
        <aside className="hidden w-[280px] border-r bg-background p-4 md:flex">
          <div className="w-full space-y-6">
            <div className="flex items-center gap-4 px-2">
              <div className="size-10 animate-pulse rounded-lg bg-muted" />
              <div className="h-6 w-28 animate-pulse rounded bg-muted" />
            </div>
            <div className="space-y-1">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="flex items-center gap-4 px-3 py-2">
                  <div className="size-5 animate-pulse rounded bg-muted" />
                  <div className="h-4 w-24 animate-pulse rounded bg-muted" />
                </div>
              ))}
            </div>
          </div>
        </aside>
        {/* Main content skeleton */}
        <div className="flex flex-1 flex-col overflow-hidden">
          <header className="flex h-16 items-center justify-between border-b bg-background px-6">
            <div className="h-8 w-64 animate-pulse rounded bg-muted" />
            <div className="flex items-center gap-4">
              <div className="size-10 animate-pulse rounded-full bg-muted" />
            </div>
          </header>
          <main className="flex-1 overflow-y-auto p-6">
            <div className="space-y-6">
              <div className="h-8 w-48 animate-pulse rounded bg-muted" />
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="space-y-4 rounded-lg border bg-card p-6">
                    <div className="h-4 w-24 animate-pulse rounded bg-muted" />
                    <div className="h-8 w-20 animate-pulse rounded bg-muted" />
                  </div>
                ))}
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <PortalProvider>
        <KeyboardShortcutsProvider>
          <NetworkStatusBanner />
          <OfflineIndicator />
          {showWelcome && userRole && (
            <FirstTimeWizard
              userRole={userRole}
              onComplete={handleWelcomeComplete}
              onSkip={handleWelcomeSkip}
            />
          )}
          <RouterTracker />
          <ScrollToTop />
          <PageTransitionLoader />
          {/* Prefetch likely next routes for faster navigation */}
          {import.meta.env.PROD && <RoutePrefetcher />}
          <Routes>
          {/* Root route - redirect to auth */}
          <Route path="/" element={<Navigate to="/auth" replace />} />

          {/* Marketing Pages - redirect to marketing site */}
          <Route path="/contact" element={<MarketingRedirect path="/contact" />} />
          <Route path="/waitlist" element={<MarketingRedirect path="/waitlist" />} />
          <Route path="/for-buyers" element={<MarketingRedirect path="/for-buyers" />} />
          <Route path="/for-suppliers" element={<MarketingRedirect path="/for-suppliers" />} />
          <Route path="/how-it-works" element={<MarketingRedirect path="/how-it-works" />} />
          <Route path="/features" element={<MarketingRedirect path="/features" />} />
          <Route path="/resources" element={<MarketingRedirect path="/resources" />} />
          <Route path="/about" element={<MarketingRedirect path="/about" />} />
          <Route path="/blog" element={<MarketingRedirect path="/blog" />} />
          <Route path="/faq" element={<MarketingRedirect path="/faq" />} />
          <Route path="/glossary" element={<MarketingRedirect path="/glossary" />} />
          <Route path="/terms" element={<MarketingRedirect path="/terms" />} />
          <Route path="/privacy" element={<MarketingRedirect path="/privacy" />} />
          <Route path="/cookies" element={<MarketingRedirect path="/privacy" />} />
          <Route path="/pricing" element={<MarketingRedirect path="/pricing" />} />
          <Route path="/investors" element={<MarketingRedirect path="/investors" />} />
          <Route path="/feature-requests" element={<PublicRouteWrapper><FeatureRequests /></PublicRouteWrapper>} />
          <Route path="/status" element={<PublicRouteWrapper><Status /></PublicRouteWrapper>} />
          
          {/* App Entry */}
          <Route path="/app" element={<Index />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/auth/pending" element={<PendingReview />} />
          <Route path="/reset-password" element={
            <PublicRouteWrapper>
              <ResetPasswordPage />
            </PublicRouteWrapper>
          } />
          {/* Redirect signup/register routes to waitlist when signups are disabled */}
          <Route path="/signup" element={<Navigate to="/waitlist" replace />} />
          <Route path="/register" element={<Navigate to="/waitlist" replace />} />
          <Route path="/auth/callback" element={<PublicRouteWrapper><AuthCallback /></PublicRouteWrapper>} />
          {/* Canva OAuth callback - must be outside protected routes to handle session loss during redirect */}
          <Route path="/buyer/canva/callback" element={<PublicRouteWrapper><CanvaCallback /></PublicRouteWrapper>} />
          {/* Canva return navigation - handles return from Canva after design completion */}
          <Route path="/buyer/canva/return" element={<PublicRouteWrapper><CanvaReturn /></PublicRouteWrapper>} />
          <Route path="/email-verification" element={<PublicRouteWrapper><EmailVerification /></PublicRouteWrapper>} />
          <Route path="/two-factor-setup" element={<PublicRouteWrapper><TwoFactorSetup /></PublicRouteWrapper>} />
          <Route path="/profile" element={
            <ProtectedRouteWrapper requiredRole={USER_ROLES.BUYER}>
              <UserProfile />
            </ProtectedRouteWrapper>
          } />
          <Route path="/setup-admin" element={
            <ProtectedRouteWrapper requiredRole={USER_ROLES.ADMIN}>
              <SetupAdmin />
            </ProtectedRouteWrapper>
          } />
          {/* Redirect /search to buyer suppliers (requires auth) */}
          <Route path="/search" element={<Navigate to="/auth?redirect=/buyer/suppliers" replace />} />
          {/* Legacy vendor routes - redirect to supplier portal */}
          <Route path="/vendor/signup" element={<Navigate to="/auth" replace />} />
          <Route path="/vendor/dashboard" element={<Navigate to="/supplier/dashboard" replace />} />
          <Route path="/vendor/profile/edit" element={<Navigate to="/supplier/profile" replace />} />
          
          {/* Buyer Portal - Protected */}
          <Route path="/buyer/*" element={
            <ProtectedRouteWrapper requiredRole={USER_ROLES.BUYER}>
              <BuyerLayout />
            </ProtectedRouteWrapper>
          }>
            <Route index element={<Navigate to="/buyer/dashboard" replace />} />
            <Route path="dashboard" element={<EnhancedBuyerDashboard />} />
            <Route path="rfq/create" element={<Navigate to="/buyer/rfq/new" replace />} />
            <Route path="rfq/new" element={<CreateRFQ />} />
            <Route path="rfq/:id" element={<BuyerRFQDetail />} />
            <Route path="rfqs/bulk" element={<BulkRFQ />} />
            <Route path="rfqs/:id" element={<BuyerRFQDetail />} />
            <Route path="analytics" element={<BuyerAnalytics />} />
            <Route path="spec-wizard" element={<SpecWizard />} />
            <Route path="saved-specs" element={<SavedSpecs />} />
            <Route path="clearance" element={<Clearance />} />
            <Route path="vendors" element={<VendorManagement />} />
            <Route path="vendors/:vendorId/history" element={<VendorHistory />} />
            <Route path="post-rfq" element={<Navigate to="/buyer/rfq/new" replace />} />
            <Route path="rfqs" element={<RFQManagement />} />
            <Route path="quotes" element={<Quotes />} />
            <Route path="quotes/compare" element={<CompareQuotes />} />
            <Route path="orders" element={<BuyerOrders />} />
            <Route path="orders/track" element={<TrackOrders />} />
            <Route path="reorder" element={<Reorder />} />
            <Route path="history" element={<Navigate to="/buyer/orders?view=history" replace />} />
            <Route path="messages" element={<Messages />} />
            <Route path="favorites" element={<Favorites />} />
            <Route path="mockup-editor" element={<MockupEditor />} />
            <Route path="products" element={<ProductsBrowse />} />
            <Route path="suppliers" element={<Suppliers />} />
            <Route path="suppliers/search" element={<Navigate to="/buyer/suppliers" replace />} />
            <Route path="suppliers/:id" element={<SupplierProfile />} />
            <Route path="suppliers/:supplierId/scorecard" element={<SupplierScorecard />} />
            <Route path="suppliers/compare" element={<SupplierComparison />} />
            <Route path="profile" element={<BuyerProfilePage />} />
            <Route path="budgets" element={<Budgets />} />
            <Route path="samples" element={<Samples />} />
            <Route path="samples-analytics" element={<BuyerSampleRequestsAnalytics />} />
            <Route path="inventory" element={<BuyerInventory />} />
            <Route path="explore" element={<ExploreProducts />} />
            <Route path="warehouse" element={<WarehouseDashboard />} />
              <Route path="procurement" element={<ProcurementOps />} />
            <Route path="inventory/analytics" element={<InventoryAnalytics />} />
            <Route path="settings" element={<BuyerSettings />} />
            <Route path="settings/team" element={<TeamManagement />} />
            <Route path="support" element={<BuyerSupport />} />
            <Route path="support/:ticketId" element={<BuyerSupportTicketDetail />} />
            <Route path="payment-methods" element={<PaymentMethods />} />
            <Route path="notifications" element={<BuyerNotifications />} />
            <Route path="help" element={<BuyerHelp />} />
            <Route path="resources" element={<BuyerResources />} />
            <Route path="resources/:category/:articleSlug" element={<BuyerResources />} />
            <Route path="suppliers/recommended" element={<RecommendedSuppliers />} />
            <Route path="search" element={<BuyerSearch />} />
            <Route path="mockup-studio" element={<MockupStudio />} />
            <Route path="mockups" element={<Navigate to="/buyer/mockup-studio" replace />} />
            <Route path="logos" element={<LogoLibrary />} />
            <Route path="designs" element={<DesignGallery />} />
            <Route path="art-files" element={<ArtFileManager />} />
            <Route path="crm-integration" element={<CRMIntegration />} />
            {/* Canva Editor deprecated - redirect to catalog for integrated workflow */}
            <Route path="canva-editor" element={<Navigate to="/buyer/catalog" replace />} />
            {FEATURE_FLAGS.designStudioV1 && DesignStudio && (
              <Route path="design-studio" element={<DesignStudio />} />
            )}
            {FEATURE_FLAGS.creditSystem && BuyerCreditsDashboard && (
              <Route path="credits" element={<BuyerCreditsDashboard />} />
            )}
            {/* Simplified Mockup Platform MVP */}
            <Route path="product-catalog" element={<Navigate to="/buyer/catalog" replace />} />
            <Route path="mockup/:productId" element={<MockupEditor />} />
            <Route path="catalog" element={<ProductCatalog />} />
            <Route path="catalog/:id" element={<BuyerProductDetail />} />
            <Route path="favorite-products" element={<FavoriteProducts />} />
            <Route path="favorite-suppliers" element={<FavoriteSuppliers />} />
            <Route path="approved-vendors" element={<ApprovedVendors />} />
            <Route path="bulk-calculator" element={<BulkDiscountCalculator />} />
            <Route path="spend-forecasting" element={<SpendForecasting />} />
            <Route path="contacts" element={<BuyerContacts />} />
            <Route path="contacts/:id" element={<BuyerContactDetail />} />
            <Route path="*" element={<NotFound />} />
          </Route>

          {/* Supplier Portal - Protected */}
          <Route path="/supplier/*" element={
            <ProtectedRouteWrapper requiredRole={USER_ROLES.SUPPLIER}>
              <SupplierLayout />
            </ProtectedRouteWrapper>
          }>
            <Route index element={<Navigate to="/supplier/dashboard" replace />} />
            <Route path="dashboard" element={<SupplierDashboard />} />
            <Route path="analytics" element={<SupplierAnalytics />} />
            <Route path="analytics/win-loss" element={<SupplierWinLossAnalytics />} />
            <Route path="team-analytics" element={<TeamAnalytics />} />
            <Route path="products" element={<SupplierProducts />} />
            <Route path="inventory" element={<VendorInventory />} />
            <Route path="rfq-marketplace" element={<RFQMarketplace />} />
            <Route path="rfqs" element={<Navigate to="/supplier/rfq-marketplace" replace />} />
            <Route path="quotes" element={<SupplierQuotes />} />
            <Route path="orders" element={<SupplierOrders />} />
            <Route path="orders/:id" element={<OrderDetail />} />
            <Route path="operations" element={<SupplierOperationsHub />} />
            <Route path="production-scheduling" element={<ProductionScheduling />} />
            <Route path="messages" element={<Messages />} />
            <Route path="profile" element={<SupplierProfilePage />} />
            <Route path="support" element={<SupplierSupport />} />
            <Route path="support/:ticketId" element={<SupplierSupportTicketDetail />} />
            <Route path="verification" element={<SupplierVerification />} />
            <Route path="settings" element={<SupplierSettings />} />
            <Route path="earnings" element={<SupplierEarnings />} />
            {/* Design mockups deprecated - redirect to dashboard */}
            <Route path="design-requests" element={<Navigate to="/supplier/dashboard" replace />} />
            <Route path="design-requests/:id" element={<Navigate to="/supplier/dashboard" replace />} />
            <Route path="sample-requests" element={<SupplierSampleRequests />} />
            <Route path="sample-requests/:id" element={<SampleRequestDetail />} />
            <Route path="sample-requests-analytics" element={<Navigate to="/supplier/sample-requests-analytics-reports" replace />} />
            <Route path="sample-requests-reports" element={<Navigate to="/supplier/sample-requests-analytics-reports" replace />} />
            <Route path="sample-requests-analytics-reports" element={<SupplierSampleRequestsAnalyticsAndReports />} />
            <Route path="customers" element={<SupplierCustomers />} />
            <Route path="customers/:id" element={<SupplierCustomerDetail />} />
            <Route path="notifications" element={<SupplierNotifications />} />
            <Route path="help" element={<SupplierHelp />} />
            <Route path="resources" element={<SupplierResources />} />
            {FEATURE_FLAGS.creditSystem && SupplierCreditsDashboard && (
              <Route path="credits" element={<SupplierCreditsDashboard />} />
            )}
            <Route path="resources/:category/:articleSlug" element={<SupplierResources />} />
            <Route path="compliance" element={<CompliancePortal />} />
            <Route path="auto-quote-rules" element={<AutoQuoteRules />} />
            <Route path="territory" element={<SupplierTerritory />} />
            <Route path="pricing-tools" element={<SupplierPricingTools />} />
            <Route path="search" element={<SupplierSearch />} />
            <Route path="crm-export" element={<SupplierCRMExport />} />
            <Route path="*" element={<NotFound />} />
          </Route>

          {/* Shared/Public routes */}
          <Route path="/products" element={<PublicRouteWrapper><ProductsBrowse /></PublicRouteWrapper>} />
          <Route path="/product/:id" element={<PublicRouteWrapper><ProductDetail /></PublicRouteWrapper>} />
          <Route path="/rfq/new" element={<Navigate to="/buyer/rfq/new" replace />} />
          <Route path="/rfq" element={<PublicRouteWrapper><RFQRouter /></PublicRouteWrapper>} />
          <Route path="/rfq/:id" element={<PublicRouteWrapper><RFQDetail /></PublicRouteWrapper>} />
          <Route path="/search/suppliers" element={<Navigate to="/buyer/suppliers" replace />} />
          <Route path="/supplier/:id" element={
            <ErrorBoundary
              FallbackComponent={ErrorFallback}
              onReset={() => window.location.reload()}
            >
              <Suspense fallback={<LazyLoadingState />}>
                <ConditionalLayout>
                  <SupplierProfile />
                </ConditionalLayout>
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/suppliers/:id" element={
            <ErrorBoundary
              FallbackComponent={ErrorFallback}
              onReset={() => window.location.reload()}
            >
              <Suspense fallback={<LazyLoadingState />}>
                <ConditionalLayout>
                  <SupplierProfile />
                </ConditionalLayout>
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/buyer/:id" element={<PublicRouteWrapper><BuyerProfile /></PublicRouteWrapper>} />
          <Route path="/orders/:id" element={
            <ProtectedRouteWrapper requiredRole={USER_ROLES.BUYER}>
              <OrderDetail />
            </ProtectedRouteWrapper>
          } />
          
          {/* Admin Portal - Protected */}
          <Route path="/admin/*" element={
            <ProtectedRouteWrapper requiredRole={USER_ROLES.ADMIN}>
              <AdminLayout />
            </ProtectedRouteWrapper>
          }>
            <Route index element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="products" element={<ProductOversight />} />
            <Route path="email-management" element={<EmailManagement />} />
            <Route path="email-templates/:id/preview" element={<EmailTemplatePreview />} />
            <Route path="email-templates/:id" element={<EmailTemplateEditor />} />
            <Route path="review-queue" element={<ReviewQueue />} />
            <Route path="verification-queue" element={<Navigate to="/admin/review-queue?tab=verifications" replace />} />
            <Route path="vendor-applications" element={
              FEATURE_FLAGS.vendorApplicationsStandalone ? <VendorApplicationsQueue /> : <Navigate to="/admin/review-queue?tab=applications" replace />
            } />
            <Route path="compliance" element={<ComplianceManagement />} />
            <Route path="support" element={<SupportTickets />} />
            <Route path="support/:ticketId" element={<SupportTicketDetail />} />
            <Route path="tracking-lookup" element={<TrackingLookup />} />
            <Route path="messages" element={<Messages />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="orders/:id" element={<OrderDetail />} />
            <Route path="rfqs" element={<AdminRFQs />} />
            <Route path="territories" element={<TerritoryManagement />} />
            <Route path="data-management" element={<DataManagement />} />
            <Route path="data-audit" element={<DataAudit />} />
            <Route path="seed-product-images" element={<SeedProductImages />} />
            {/* Redirect old seed data routes to unified Data Management */}
            <Route path="seed" element={<Navigate to="/admin/data-management" replace />} />
            <Route path="seed-comprehensive" element={<Navigate to="/admin/data-management" replace />} />
            <Route path="seed-verification" element={<Navigate to="/admin/data-management" replace />} />
            <Route path="cleanup-seed-data" element={<Navigate to="/admin/data-management" replace />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="analytics/executive" element={<ExecutiveDashboard />} />
            <Route path="analytics/financial" element={<FinancialDashboard />} />
            <Route path="analytics/pipeline" element={<PipelineDashboard />} />
            <Route path="analytics/customers" element={<CustomerHealthDashboard />} />
            <Route path="analytics/payment-failures" element={<PaymentFailureAnalytics />} />
            <Route path="error-logs" element={<ErrorLogs />} />
            <Route path="security" element={<SecurityDashboard />} />
            <Route path="fraud-detection" element={<FraudDetection />} />
            <Route path="testing" element={<TestingDashboard />} />
            <Route path="admin-settings" element={<AdminSettings />} />
            <Route path="system-status" element={<SystemStatus />} />
            <Route path="trending-management" element={<TrendingManagement />} />
            <Route path="profile" element={<AdminProfilePage />} />
            <Route path="settings" element={<Settings />} />
            <Route path="notifications" element={<AdminNotifications />} />
            <Route path="notifications/new" element={<NewBroadcast />} />
            <Route path="populate-images" element={<PopulateProductImages />} />
            <Route path="model-library" element={<ModelLibrary />} />
            <Route path="cron-monitoring" element={<CronMonitoring />} />
            <Route path="navigation-audit" element={<NavigationAudit />} />
            <Route path="audit-log" element={<AuditLog />} />
            <Route path="system-logs" element={<SystemLogs />} />
            <Route path="win-loss-analytics" element={<AdminWinLossAnalytics />} />
            <Route path="search" element={<AdminSearch />} />
            <Route path="*" element={<NotFound />} />
          </Route>
          
          {/* Catch-all */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </KeyboardShortcutsProvider>
      </PortalProvider>
    </BrowserRouter>
  );
}

// Safe TooltipProvider wrapper with error handling
// This ensures the app can still render even if TooltipProvider fails to load
const SafeTooltipProvider = ({ children }: { children: React.ReactNode }) => {
  // TooltipProvider is imported at the top, so it should be available
  // If there's an issue, React's error boundary will catch it
  // For now, we'll just use TooltipProvider directly since it's a static import
  return <TooltipProvider>{children}</TooltipProvider>;
};

const App = () => {
  // Hide loading screen once React has mounted - ensures we don't show blank screen
  useEffect(() => {
    if (typeof window.hsAppReady === 'function') {
      window.hsAppReady();
    }
  }, []);

  // Build marker for deployment verification - only log in development
  if (import.meta.env.DEV) {
    logger.info('[HoleScale] App component rendering - Build: 2024-12-07-v1');
  }

  // Check for missing environment configuration before rendering anything
  if (!isSupabaseConfigured) {
    if (import.meta.env.DEV) {
      logger.warn('[HoleScale] Supabase not configured, showing error');
    }
    return <ConfigurationError missingVariables={missingEnvVars} />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <VideoProviderWrapper>
          <ImpersonationProvider>
            <ResponsiveProvider>
              <SafeTooltipProvider>
                <Toaster />
                <Sonner />
                <AppContent />
                <MobileDesktopNotice />
                {import.meta.env.DEV && <ProductionDebugger />}
              </SafeTooltipProvider>
            </ResponsiveProvider>
          </ImpersonationProvider>
        </VideoProviderWrapper>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
